import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Md5 } from 'ts-md5';
import { HttpErrorResponse } from '@angular/common/http';
import {
  CountdownTimerPipe,
  ErrorComponent,
  NimmstaCommonsModuleConfiguration,
  Worker,
} from '@nimmsta/ng-commons-lib';
import { WorkerService } from '../../services/worker.service';
import { QRStatus } from '../../services/response/QRResponse';
import { TranslatePipe } from '@ngx-translate/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { QRCodeComponent } from 'angularx-qrcode';
import { LaddaModule } from 'angular2-ladda';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { getPDFContent } from './paringPDF';

type RenderedQRCodeComponent = QRCodeComponent & {
  qrcElement: ElementRef<HTMLElement>;
};

@Component({
  selector: 'app-pair-worker-modal',
  templateUrl: './pair-worker-modal.component.html',
  styleUrls: ['./pair-worker-modal.component.scss'],
  imports: [
    TranslatePipe,
    MatProgressSpinner,
    QRCodeComponent,
    LaddaModule,
    TranslatePipe,
    ErrorComponent,
    CountdownTimerPipe,
  ],
})
export class PairWorkerModalComponent implements OnInit {
  private workerService = inject(WorkerService);
  modalRef = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(
    DynamicDialogConfig<{ worker: Worker }>,
  );
  protected configuration = inject(NimmstaCommonsModuleConfiguration);

  worker!: Worker;

  error?: HttpErrorResponse;

  qrCode?: string;

  qrCodeId?: string;

  expires?: Date;

  timeRemaining = 0;

  userQRData?: string;

  profileImg?: string;

  isQRLoading = false;

  latestAppDownloadLink?: string;

  @ViewChild('downloadQR') downloadQR!: RenderedQRCodeComponent;
  @ViewChild('loginQR') loginQR!: RenderedQRCodeComponent;

  private interval?: ReturnType<typeof setInterval> = undefined;

  constructor() {
    this.worker = this.dialogConfig.data!.worker;
    if (
      this.configuration.apiUrls.workflow &&
      this.configuration.androidAppSoftwareId
    ) {
      this.latestAppDownloadLink =
        `${this.configuration.apiUrls.workflow}/api/v1/software/` +
        `${this.configuration.androidAppSoftwareId}/latest-download`;
    }
  }

  ngOnInit(): void {
    pdfMake.addVirtualFileSystem(pdfFonts);
    this.modalRef.onClose.subscribe(() => {
      clearInterval(this.interval!);
    });
    this.refreshQrCode();
  }

  refreshQrCode(): void {
    this.qrCode = undefined;
    this.qrCodeId = undefined;
    this.expires = undefined;
    clearInterval(this.interval!);
    this.workerService.createQRCode(this.worker, false).subscribe({
      next: (response) => {
        this.qrCode = response.qrCode;
        this.qrCodeId = response.id;
        this.expires = new Date(response.expiresAt);
        this.timeRemaining = this.expires.getTime() - new Date().getTime();
        this.interval = setInterval(() => {
          this.timeRemaining -= 1000;
          this.checkUsed();
          if (this.timeRemaining <= 0) {
            clearInterval(this.interval!);
          }
        }, 1000);
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  checkUsed(): void {
    this.workerService.getQRCode(this.qrCodeId!).subscribe({
      next: (response) => {
        if (response.status === QRStatus.USED) {
          clearInterval(this.interval!);
          this.modalRef.close();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  printQR(): void {
    this.isQRLoading = true;
    clearInterval(this.interval!);
    this.profileImg = `https://www.gravatar.com/avatar/${Md5.hashStr(
      this.worker.email ?? '',
    )}`;
    this.workerService.createQRCode(this.worker, true).subscribe((response) => {
      this.userQRData = response.qrCode;
      // Wait for Angulars change detection
      setTimeout(() => {
        void this.openPDF();
      }, 0);
    });
  }

  async openPDF(): Promise<void> {
    const logo64 = await this.getBase64FromAsset('assets/img/logo.png');
    const downloadQR64 = this.getQRCodeImageSource(this.downloadQR);
    const loginQR64 = this.getQRCodeImageSource(this.loginQR);

    const pdf = pdfMake.createPdf(
      getPDFContent(
        logo64,
        downloadQR64,
        loginQR64,
        this.worker.given_name,
        this.worker.family_name,
      ),
    );
    const blob = await pdf.getBlob();
    window.open(URL.createObjectURL(blob));
    this.isQRLoading = false;
  }

  private getQRCodeImageSource(qrCode: RenderedQRCodeComponent): string {
    return qrCode.qrcElement.nativeElement.querySelector('img')!.src;
  }

  private getBase64FromAsset(assetPath: string): Promise<string> {
    return fetch(assetPath)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          }),
      );
  }
}
